import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Route,
  MapPin,
  Clock,
  Gauge,
  Timer,
  Users,
  StopCircle,
  Play,
  Building2,
} from 'lucide-react';

interface VehicleOption {
  id: string;
  name: string;
  vehicle_number: string;
}

interface VehicleListData {
  vehicles: VehicleOption[];
}

interface Stop {
  latitude: number;
  longitude: number;
  arrived_at: string;
  departed_at: string;
  duration_minutes: number;
  client_name?: string;
  geofence_name?: string;
  is_client_visit: boolean;
}

interface RouteReplaySegment {
  latitude: number;
  longitude: number;
  speed: number;
  timestamp: string;
  event_type?: string;
  is_stop: boolean;
  stop_duration_minutes?: number;
  client_name?: string;
}

interface RouteStats {
  total_miles: number;
  total_time_minutes: number;
  idle_time_minutes: number;
  max_speed: number;
  avg_speed: number;
  stop_count: number;
  client_visits: number;
}

interface RouteReplayResult {
  vehicle_id: string;
  vehicle_name: string;
  date: string;
  segments: RouteReplaySegment[];
  stops: Stop[];
  stats: RouteStats;
}

const MOCK_VEHICLES: VehicleListData = {
  data: [
    { id: 'v1', name: 'Unit 101 - Ford F-150', vehicle_number: '101' },
    { id: 'v2', name: 'Unit 102 - Chevy Silverado', vehicle_number: '102' },
    { id: 'v3', name: 'Unit 103 - Ram 1500', vehicle_number: '103' },
  ],
};

const MOCK_REPLAY: RouteReplayResult = {
  vehicle_id: 'v1',
  vehicle_name: 'Unit 101 - Ford F-150',
  date: '2026-03-10',
  stats: {
    total_miles: 142.4,
    total_time_minutes: 435,
    idle_time_minutes: 95,
    max_speed: 72,
    avg_speed: 38,
    stop_count: 5,
    client_visits: 3,
  },
  stops: [
    { latitude: 33.749, longitude: -84.388, arrived_at: '2026-03-10T08:15:00Z', departed_at: '2026-03-10T09:30:00Z', duration_minutes: 75, client_name: 'Acme Corp', geofence_name: 'Acme HQ', is_client_visit: true },
    { latitude: 33.752, longitude: -84.391, arrived_at: '2026-03-10T10:45:00Z', departed_at: '2026-03-10T10:55:00Z', duration_minutes: 10, is_client_visit: false },
    { latitude: 33.745, longitude: -84.385, arrived_at: '2026-03-10T12:00:00Z', departed_at: '2026-03-10T12:45:00Z', duration_minutes: 45, client_name: 'Globex Inc', geofence_name: 'Globex Office', is_client_visit: true },
    { latitude: 33.748, longitude: -84.393, arrived_at: '2026-03-10T13:30:00Z', departed_at: '2026-03-10T13:35:00Z', duration_minutes: 5, is_client_visit: false },
    { latitude: 33.751, longitude: -84.380, arrived_at: '2026-03-10T15:20:00Z', departed_at: '2026-03-10T16:30:00Z', duration_minutes: 70, client_name: 'Initech Ltd', geofence_name: 'Initech Site', is_client_visit: true },
  ],
  segments: [],
};

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function speedColor(speed: number): string {
  if (speed === 0) return '#6B7280';
  if (speed < 25) return '#10B981';
  if (speed < 45) return '#3B82F6';
  if (speed < 65) return '#F59E0B';
  return '#EF4444';
}

function speedLabel(speed: number): string {
  if (speed === 0) return 'Stopped';
  if (speed < 25) return 'Slow';
  if (speed < 45) return 'Normal';
  if (speed < 65) return 'Fast';
  return 'Over Limit';
}

export default function RouteReplayPage() {
  const { data: vehiclesData } = useApi<VehicleListData>('/api/vehicles');
  const vehicleOptions = vehiclesData?.vehicles ?? MOCK_VEHICLES.data;

  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().substring(0, 10));
  const [replayData, setReplayData] = useState<RouteReplayResult | null>(MOCK_REPLAY);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'segments' | 'stops'>('stops');

  const handleLoad = async () => {
    if (!selectedVehicleId || !selectedDate) return;
    setIsLoading(true);
    try {
      const resp = await fetch(
        `/api/route-replay/${selectedVehicleId}?date=${selectedDate}`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const json = await resp.json() as RouteReplayResult;
        setReplayData(json);
      }
    } catch { /* ignore */ }
    setIsLoading(false);
  };

  const stats = replayData?.stats;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Route Replay & Analysis"
        description="Review vehicle routes, stops, and driving patterns by date"
      />

      {/* Controls */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Vehicle</label>
              <select
                value={selectedVehicleId}
                onChange={e => setSelectedVehicleId(e.target.value)}
                className="block w-52 px-3 py-2 rounded-lg text-sm border bg-transparent"
                style={{ color: 'var(--foreground)', borderColor: 'var(--glass-border)' }}
              >
                <option value="">Select vehicle...</option>
                {vehicleOptions.map(v => (
                  <option key={v.id} value={v.id}>#{v.vehicle_number} — {v.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="block px-3 py-2 rounded-lg text-sm border bg-transparent"
                style={{ color: 'var(--foreground)', borderColor: 'var(--glass-border)' }}
              />
            </div>

            <button
              type="button"
              onClick={handleLoad}
              disabled={isLoading || !selectedVehicleId}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(16,185,129,0.2)',
                color: '#10B981',
                border: '1px solid rgba(16,185,129,0.3)',
                opacity: (!selectedVehicleId || isLoading) ? 0.5 : 1,
              }}
            >
              {isLoading ? <Spinner size={14} /> : <Play className="w-4 h-4" />}
              {isLoading ? 'Loading...' : 'Load Route'}
            </button>
          </div>
        </CardContent>
      </Card>

      {replayData && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard icon={Route} label="Total Miles" value={`${stats!.total_miles} mi`} />
            <StatCard icon={Clock} label="Drive Time" value={formatMinutes(stats!.total_time_minutes - stats!.idle_time_minutes)} />
            <StatCard icon={StopCircle} label="Stops" value={stats!.stop_count} />
            <StatCard icon={Timer} label="Idle Time" value={formatMinutes(stats!.idle_time_minutes)} />
            <StatCard icon={Gauge} label="Max Speed" value={`${stats!.max_speed} mph`} accent={stats!.max_speed >= 65 ? 'destructive' : undefined} />
            <StatCard icon={Users} label="Client Visits" value={stats!.client_visits} accent="success" />
          </div>

          {/* Tabs */}
          <Card>
            <CardContent className="p-0">
              {/* Tab Header */}
              <div className="flex items-center gap-1 px-5 pt-4 pb-0" style={{ borderBottom: '1px solid var(--glass-border)' }}>
                {(['stops', 'segments'] as const).map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className="px-4 py-2 text-sm font-medium capitalize transition-colors rounded-t-lg -mb-px"
                    style={{
                      color: activeTab === tab ? 'var(--foreground)' : 'var(--muted-foreground)',
                      borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                    }}
                  >
                    {tab === 'stops' ? `Stops & Visits (${replayData.stops.length})` : 'Speed Segments'}
                  </button>
                ))}
              </div>

              {/* Stops Tab */}
              {activeTab === 'stops' && (
                <div className="divide-y" style={{ '--tw-divide-opacity': 1 } as React.CSSProperties}>
                  {replayData.stops.length === 0 && (
                    <p className="px-5 py-8 text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>
                      No stops detected for this route.
                    </p>
                  )}
                  {replayData.stops.map((stop, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 px-5 py-4"
                      style={{ borderBottom: '1px solid var(--glass-border)' }}
                    >
                      <div
                        className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 mt-0.5"
                        style={{
                          background: stop.is_client_visit ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)',
                        }}
                      >
                        {stop.is_client_visit ? (
                          <Building2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {stop.client_name ? (
                            <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                              {stop.client_name}
                            </span>
                          ) : (
                            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Unknown Stop</span>
                          )}
                          {stop.geofence_name && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: 'var(--glass-bg)', color: 'var(--muted-foreground)', border: '1px solid var(--glass-border)' }}
                            >
                              {stop.geofence_name}
                            </span>
                          )}
                          {stop.is_client_visit && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                              Client Visit
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(stop.arrived_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' — '}
                            {new Date(stop.departed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>
                            {formatMinutes(stop.duration_minutes)}
                          </span>
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {stop.latitude.toFixed(4)}, {stop.longitude.toFixed(4)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Segments Tab */}
              {activeTab === 'segments' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        {['Time', 'Location', 'Speed', 'Event', 'Stop Duration', 'Client'].map(col => (
                          <th
                            key={col}
                            className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wide"
                            style={{ color: 'var(--muted-foreground)' }}
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {replayData.segments.slice(0, 100).map((seg, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: '1px solid var(--glass-border)' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                        >
                          <td className="px-5 py-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(seg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-5 py-2 text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                            {seg.latitude.toFixed(4)}, {seg.longitude.toFixed(4)}
                          </td>
                          <td className="px-5 py-2">
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{ background: `${speedColor(seg.speed)}20`, color: speedColor(seg.speed) }}
                            >
                              {seg.speed} mph — {speedLabel(seg.speed)}
                            </span>
                          </td>
                          <td className="px-5 py-2 text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>
                            {seg.event_type?.replace(/_/g, ' ') ?? '—'}
                          </td>
                          <td className="px-5 py-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {seg.stop_duration_minutes ? formatMinutes(seg.stop_duration_minutes) : '—'}
                          </td>
                          <td className="px-5 py-2 text-xs" style={{ color: seg.client_name ? '#10B981' : 'var(--muted-foreground)' }}>
                            {seg.client_name ?? '—'}
                          </td>
                        </tr>
                      ))}
                      {replayData.segments.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-5 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                            No GPS segment data available for this date.
                          </td>
                        </tr>
                      )}
                      {replayData.segments.length > 100 && (
                        <tr>
                          <td colSpan={6} className="px-5 py-3 text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            Showing 100 of {replayData.segments.length} segments
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Speed Legend */}
          <div
            className="flex items-center gap-4 flex-wrap p-4 rounded-xl"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(var(--glass-blur))',
            }}
          >
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Speed Legend:</span>
            {[
              { label: 'Stopped', color: '#6B7280' },
              { label: '< 25 mph', color: '#10B981' },
              { label: '25–45 mph', color: '#3B82F6' },
              { label: '45–65 mph', color: '#F59E0B' },
              { label: '65+ mph', color: '#EF4444' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item.color }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
